import {
  invalidateSessionsQuery,
  invalidateTeamMemberSessionsQuery,
} from '@/hooks/query/useQueryCombinedSessions';
import { deleteMemberPatientProfile } from '@/pages/api/scribeAPIV2';
import { logger } from '@/utils/logger/logger';
import { toast } from '@heidi/design-system/toast';
import {
  type DeletePatientProfileArgs,
  type PatientDeleteBeRefusalReason,
  useMutateDeleteOwnPatientProfile as useMutateDeleteOwnPatientProfileCore,
} from '@heidi/patients/data';
import { GET_SESSION_BY_ID, type QuerySessionResponse } from '@heidi/session/contracts';
import {
  selectedReviewMemberAtom,
  useCombinedQueriesTeamGeneralInfo,
} from '@heidi/workspace/data';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { GET_TEAM_MEMBER_PATIENT_PROFILES } from '../query/useQueryPatientProfiles';
import { useRBACPermissionCheck } from '../useRBACPermissionCheck';

/**
 * Clear the deleted patient's snapshot from any cached own-session-by-id
 * query. Called on the unlink path only — when the BE cascades, sessions
 * are soft-deleted and the natural sessions-list invalidation removes
 * them entirely; patching their patient_profile / session_name would flash
 * an empty header until the row drops out. On unlink the sessions survive
 * but still carry the stale patient snapshot, so we apply the optimistic
 * update pattern used in `useMutateUnlinkSessionsToPatientProfile`. The
 * team-member endpoint always cascades server-side, so this helper only
 * targets `GET_SESSION_BY_ID`.
 */
const clearPatientFromCachedSessions = (
  queryClient: ReturnType<typeof useQueryClient>,
  patientProfileId: string
) => {
  queryClient.setQueriesData<QuerySessionResponse>(
    {
      queryKey: [GET_SESSION_BY_ID],
      predicate: (query) => {
        const data = query.state.data as QuerySessionResponse | undefined;
        return data?.patient_profile?.id === patientProfileId;
      },
    },
    (oldData) => {
      if (!oldData) return oldData;
      // Mirror what the BE actually clears on unlink:
      // `unlink_patient_from_sessions` nulls patient_profile,
      // extracted_patient_context, and the embedded
      // `patient.{name, demographic_details, dob, additional_context}` subdoc,
      // but intentionally leaves the top-level `session_name` alone — it can
      // be user-edited independently of the patient and survives the unlink.
      // Touching it here would flash an empty header until the natural
      // sessions-list refetch resolves with the real (preserved) name.
      //
      // Clearing extracted_patient_context is what hides the auto-extract
      // suggestion badge + popup: that UI is driven solely by the session's
      // extracted_patient_context, independent of the patient link, so without
      // this null the suggestions linger on the now-unlinked session until a
      // GET_SESSION_BY_ID refetch lands (which this delete path doesn't force).
      return {
        ...oldData,
        patient_profile: null,
        extracted_patient_context: null,
        patient: oldData.patient
          ? {
              ...oldData.patient,
              name: null,
              demographic_details: null,
            }
          : oldData.patient,
      };
    }
  );
};

/**
 * COLAB-447: `onDeleteBlocked` carries a reasoned backend refusal (400 with a
 * known `reason`) to the host, whose dialog explains it. It serves the OWN
 * arm only: the team-member endpoint is a different contract that does not
 * speak this payload, so in review mode the port is accepted and never fires.
 *
 * Module-local on purpose: this hub is frozen by the patients family ratchet,
 * and the shape is only ever consumed as the hook's parameter type.
 */
interface UseMutateDeletePatientProfileOptions {
  onDeleteBlocked?: (reason: PatientDeleteBeRefusalReason) => void;
}

export function useMutateDeletePatientProfile(
  options: UseMutateDeletePatientProfileOptions = {}
) {
  const { isReviewingOtherUser } = useRBACPermissionCheck();
  const ownPatientProfile = useMutateDeleteOwnPatientProfile(options);
  const teamMemberPatientProfile = useMutateDeleteTeamMemberPatientProfile();
  if (isReviewingOtherUser) {
    return teamMemberPatientProfile;
  }
  return ownPatientProfile;
}

export type { DeletePatientProfileArgs };

/**
 * App-side delegate for `@heidi/patients/data` (D2.2, ARCFE-1053).
 *
 * The port fires exactly where these reactions fired in the legacy body —
 * after the patient-profiles invalidation, before the toast — so the whole
 * sequence is preserved outright rather than argued about. All three are
 * Session's cache or a user-visible effect, i.e. app integration's (child
 * design D2 / packages/CLAUDE.md).
 *
 * `onDeleteBlocked` is threaded straight through, with no toast of its own:
 * the host's dialog is the surface for a reasoned refusal.
 */
export function useMutateDeleteOwnPatientProfile({
  onDeleteBlocked,
}: UseMutateDeletePatientProfileOptions = {}) {
  const queryClient = useQueryClient();

  return useMutateDeleteOwnPatientProfileCore({
    onDeleteBlocked,
    onDeleted: ({ patientProfile, deleteLinkedSessions = true }) => {
      // Either branch needs sessions refetched: cascade removes them, unlink
      // strips their patient_profile_id. Invalidating covers both.
      void invalidateSessionsQuery();
      // Only patch cached session-by-id snapshots on the unlink path. On
      // cascade the sessions are soft-deleted server-side; wiping
      // patient_profile / session_name on an about-to-be-removed cache
      // entry would flash an empty header until the natural refetch
      // removes the row entirely.
      if (!deleteLinkedSessions && patientProfile.id) {
        clearPatientFromCachedSessions(queryClient, patientProfile.id);
      }
      toast({
        tone: 'success',
        title: `Patient profile ${patientProfile.demographic_string} deleted`,
        // Default toast width is 400px — shrink to content so short
        // messages don't leave empty space before the close button.
        className: 'w-auto',
      });
    },
  });
}

export function useMutateDeleteTeamMemberPatientProfile() {
  const queryClient = useQueryClient();
  const { teamDetails } = useCombinedQueriesTeamGeneralInfo();
  const teamId = teamDetails?.team.id;
  const selectedReviewMember = useAtomValue(selectedReviewMemberAtom);
  return useMutation({
    mutationKey: ['DeleteTeamMemberPatientProfile'],
    // Accept the same shape as the own-patient mutation so the dispatcher in
    // `useMutateDeletePatientProfile` returns a type-compatible hook. The
    // team-member BE endpoint does not yet honor `deleteLinkedSessions` — it
    // continues to cascade — so the flag is accepted but ignored here.
    mutationFn: async ({ patientProfile }: DeletePatientProfileArgs) => {
      if (patientProfile?.id && teamId && selectedReviewMember) {
        return await deleteMemberPatientProfile(
          teamId,
          selectedReviewMember,
          patientProfile?.id
        );
      }
      return Promise.reject(new Error('Patient profile ID is required'));
    },
    onSuccess: (_, { patientProfile }) => {
      void queryClient.invalidateQueries({
        queryKey: [GET_TEAM_MEMBER_PATIENT_PROFILES, teamId, selectedReviewMember, true],
      });
      // The team-member endpoint always cascades server-side (it does not
      // yet accept `deleteLinkedSessions`), so the sessions are gone and
      // the cache predicate-clear would only race with the natural list
      // invalidation. Sessions-list invalidation alone is sufficient.
      void invalidateTeamMemberSessionsQuery(teamId, selectedReviewMember);
      toast({
        tone: 'success',
        title: `Patient profile ${patientProfile.demographic_string} deleted`,
        // Default toast width is 400px — shrink to content so short
        // messages don't leave empty space before the close button.
        className: 'w-auto',
      });
    },
    onError: (error) => {
      logger.error('Error:', error);
    },
  });
}
